import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatError, homeFor, TIER_LABEL } from "../lib/api";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono-x text-[0.68rem] tracking-[0.22em] uppercase text-[#4A5878]">{label}</span>
    <input {...props} className="mt-2 w-full bg-transparent border-b border-[#0E1B3D]/20 focus:border-[#D7263D] outline-none py-3 text-[#0E1B3D] placeholder:text-[#8792AA] transition-colors" />
  </label>
);

export default function AuthPage() {
  const [params] = useSearchParams();
  const tier = params.get("tier");
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const go = (u) => {
    if (!u.tier && u.role !== "admin") return navigate(`/join${tier ? `?tier=${tier}` : ""}`, { replace: true });
    navigate(params.get("next") || homeFor(u), { replace: true });
  };

  useEffect(() => {
    if (user) go(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = mode === "signup" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode === "signup" ? "register" : "login"}`, body);
      setUser(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    if (tier) sessionStorage.setItem("ar_pending_tier", tier);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/feed";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main data-testid="auth-page" className="min-h-screen grid lg:grid-cols-2 pt-[72px]">
      <div className="relative hidden lg:block overflow-hidden">
        <motion.img initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }} src="/img/p2.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#FFF8EE]" />
        <p className="absolute left-10 bottom-10 font-display italic text-4xl text-[#0E1B3D] max-w-sm">"Welcome in."</p>
      </div>
      <div className="relative h-40 lg:hidden overflow-hidden">
        <img src="/img/p2.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[50%_30%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#FFF8EE]" />
      </div>
      <div className="flex items-center px-6 sm:px-14 py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-md mx-auto">
          <p className="eyebrow mb-5">{mode === "signup" ? "Become a member" : "Members entrance"}</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0E1B3D]">{mode === "signup" ? "Create your account" : "Welcome back"}</h1>
          {tier && TIER_LABEL[tier] && <p data-testid="auth-selected-tier" className="mt-4 text-sm text-[#4A5878]">Selected tier: <span className="text-[#D7263D]">{TIER_LABEL[tier]}</span></p>}
          <form onSubmit={submit} className="mt-10 space-y-7">
            {mode === "signup" && <Field label="Name" data-testid="auth-modal-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />}
            <Field label="Email" data-testid="auth-modal-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            <Field label="Password" data-testid="auth-modal-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
            {error && <p data-testid="auth-error" className="text-sm text-[#C81E3A]">{error}</p>}
            <button data-testid="auth-modal-submit-btn" disabled={busy} className="gold-btn w-full py-4 text-sm font-semibold tracking-[0.14em] uppercase flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}{mode === "signup" ? "Create account" : "Sign in"}
            </button>
          </form>
          <div className="my-8 flex items-center gap-4 text-xs text-[#4A5878] font-mono-x tracking-[0.2em]"><span className="h-px flex-1 bg-[#0E1B3D]/10" />OR<span className="h-px flex-1 bg-[#0E1B3D]/10" /></div>
          <button data-testid="auth-modal-google-btn" onClick={google} className="ghost-btn w-full py-4 text-sm tracking-wide flex items-center justify-center gap-3">
            <svg viewBox="0 0 48 48" className="w-5 h-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <p className="mt-10 text-sm text-[#4A5878]">
            {mode === "signup" ? "Already a member?" : "New here?"}{" "}
            <button data-testid="auth-toggle-mode-btn" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setError(""); }} className="text-[#D7263D] hover:underline underline-offset-4">
              {mode === "signup" ? "Sign in" : "Create an account"}
            </button>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
